/**
 * ProgramRequiredModal
 *
 * Shown once when a coach lands on the Ads page (DashboardListingWrapper)
 * with zero programs. Ads must link to a program (see
 * REQUIRED_FIELD_CHECKS in DashboardListingWrapper.js — an ad can't be
 * activated without one), so a coach with no programs yet can't actually
 * get an ad live. This nudges them to create one before they hit that
 * wall later, without blocking anything — they can always dismiss and
 * browse the Ads page as normal.
 *
 * Visual language matches UpgradeRequiredModal / CompleteProfileModal —
 * same gradient card / gold accent / backdrop — so all of the "you need
 * to do X before Y" popups in the app read as one system.
 *
 * Props
 *   isOpen      (boolean) controls visibility
 *   onClose     (() => void) "Later" / X / Escape / backdrop click
 *   onCreateNow (() => void) primary CTA — parent should route the coach
 *                            to the program-creation flow
 */

import React, { useEffect } from 'react';

const ProgramRequiredModal = ({ isOpen, onClose, onCreateNow }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = e => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    onClose && onClose();
  };
  const handleCardClick = e => e.stopPropagation();

  return (
    <>
      <style>{`
        @keyframes programRequiredModalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes programRequiredModalSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes programRequiredModalPulseGlow {
          0%, 100% {
            box-shadow:
              0 4px 20px rgba(251, 191, 36, 0.45),
              0 0 0 0 rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow:
              0 6px 28px rgba(251, 191, 36, 0.6),
              0 0 0 8px rgba(251, 191, 36, 0);
          }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-required-modal-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'programRequiredModalFadeIn 0.25s ease-out',
        }}
      >
        {/* Card */}
        <div
          onClick={handleCardClick}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '520px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            padding: '32px',
            borderRadius: '20px',
            background:
              'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.32) 0%, rgba(139, 92, 246, 0) 55%), ' +
              'linear-gradient(135deg, #064E3B 0%, #0F3F4A 45%, #1E1040 100%)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
            color: '#FFFFFF',
            animation: 'programRequiredModalSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Close (X) button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {/* Eyebrow */}
          <div
            style={{
              display: 'inline-block',
              padding: '6px 14px',
              marginBottom: '18px',
              borderRadius: '999px',
              background: 'rgba(251, 191, 36, 0.18)',
              border: '1px solid rgba(251, 191, 36, 0.45)',
              color: '#FBBF24',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
            }}
          >
            Programs
          </div>

          {/* Heading */}
          <h2
            id="program-required-modal-title"
            style={{
              margin: '0 0 12px',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#FFFFFF',
            }}
          >
            A program is required
          </h2>

          {/* Body */}
          <p
            style={{
              margin: '0 0 24px',
              fontSize: '15px',
              lineHeight: 1.55,
              color: 'rgba(255, 255, 255, 0.86)',
            }}
          >
            Every ad needs to link to a program so clients know exactly what they're signing up for.
            You don't have one yet — create your first program now, or come back to it later.
          </p>

          {/* CTAs */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={onCreateNow}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '14px 22px',
                borderRadius: '12px',
                border: 'none',
                background: '#FBBF24',
                color: '#0F172A',
                fontWeight: 700,
                fontSize: '15px',
                letterSpacing: '0.2px',
                animation: 'programRequiredModalPulseGlow 2.4s ease-in-out infinite',
              }}
            >
              Create now
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '14px 18px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                background: 'transparent',
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramRequiredModal;
